'use client';

import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { createClient } from '@/lib/supabase/client';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface StatusEntry {
  name: string;
  value: number;
  color: string;
}

const COLORS: Record<string, string> = {
  online: 'hsl(142 71% 45%)',
  latenza_alta: 'hsl(38 92% 50%)',
  intermittente: 'hsl(38 92% 50%)',
  offline: 'hsl(0 84% 60%)',
  manutenzione: 'hsl(217 91% 60%)',
};

const LABELS: Record<string, string> = {
  online: 'Online',
  latenza_alta: 'Latenza Alta',
  intermittente: 'Intermittente',
  offline: 'Offline',
  manutenzione: 'Manutenzione',
};

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const total = d._total || 1;
  return (
    <div className="bg-surface-elevated border border-border rounded-xl p-3 shadow-xl text-sm">
      <p className="font-bold text-white">{d.name}</p>
      <p className="text-secondary-color mt-0.5">
        <span className="font-mono text-white">{d.value}</span> dispositivi ({((d.value / total) * 100).toFixed(1)}%)
      </p>
    </div>
  );
};

export default function DeviceStatusChart() {
  const [data, setData] = useState<StatusEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const { data: devices } = await supabase
          .from('devices')
          .select('stato');

        const counts: Record<string, number> = {};
        (devices || []).forEach((d: any) => {
          counts[d.stato] = (counts[d.stato] || 0) + 1;
        });

        const total = Object.values(counts).reduce((a, b) => a + b, 0);
        const mapped: StatusEntry[] = Object.entries(counts)
          .filter(([, v]) => v > 0)
          .map(([stato, value]) => ({
            name: LABELS[stato] || stato,
            value,
            color: COLORS[stato] || 'hsl(215 15% 50%)',
            _total: total,
          } as any));

        setData(mapped);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();

    const channel = supabase
      .channel('device_status_chart')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'devices' }, fetchStatus)
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  const total = data.reduce((s, d) => s + d.value, 0);
  const onlineCount = data.find(d => d.name === 'Online')?.value || 0;

  if (loading) {
    return (
      <div className="bg-surface border border-border rounded-xl p-5 h-full flex items-center justify-center">
        <svg className="animate-spin w-6 h-6 text-sky-400" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-border rounded-xl p-5 h-full">
      <div className="mb-4">
        <h2 className="text-base font-bold text-white">Stato Dispositivi</h2>
        <p className="text-sm text-secondary-color mt-0.5">{total} dispositivi gestiti in totale</p>
      </div>
      {total === 0 ? (
        <div className="flex items-center justify-center h-40">
          <p className="text-secondary-color text-sm">Nessun dispositivo</p>
        </div>
      ) : (
        <>
          <div className="relative">
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={72}
                  paddingAngle={3}
                  dataKey="value"
                  strokeWidth={0}
                >
                  {data.map((entry) => (
                    <Cell key={`cell-device-${entry.name}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <p className="text-2xl font-bold font-mono text-white tabular-nums">{onlineCount}</p>
                <p className="text-sm text-secondary-color">online</p>
              </div>
            </div>
          </div>
          <ul className="mt-3 space-y-2.5">
            {data.map((d) => (
              <li key={`legend-${d.name}`} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
                  <span className="text-secondary-color">{d.name}</span>
                </span>
                <span className="font-mono text-white tabular-nums font-semibold">{d.value}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}